/**
 * 配信機能 API サービス
 *
 * 管理画面向けの APIキー管理・配信状況取得を行います。
 */

import { getAuthInstance } from "../lib/firebase";
import { CUSTOMER_API_BASE_URL } from "../constants/api";
import { SerializedTimestamp } from "../types/common";

/**
 * APIキー情報
 */
export interface ApiKeyItem {
  id: string;
  key: string;
  customerId: string;
  isActive: boolean;
  createdAt: SerializedTimestamp;
  label: string;
}

/**
 * 認証ヘッダーを取得
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const auth = getAuthInstance();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("認証されていません");
  }

  const token = await user.getIdToken();

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

/**
 * APIレスポンスをハンドリング
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "不明なエラー" }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * 顧客のAPIキー一覧を取得
 */
export async function fetchApiKeys(customerId: string): Promise<ApiKeyItem[]> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/${customerId}/apiKeys`, {
    headers,
  });
  const data = await handleResponse<{ apiKeys: ApiKeyItem[] }>(response);
  return data.apiKeys;
}

/**
 * APIキーを新規発行
 */
export async function createApiKey(
  customerId: string,
  label?: string
): Promise<string> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/${customerId}/apiKeys`, {
    method: "POST",
    headers,
    body: JSON.stringify({ label }),
  });
  const data = await handleResponse<{ key: string }>(response);
  return data.key;
}

/**
 * APIキーの有効/無効を切り替え
 */
export async function toggleApiKey(
  customerId: string,
  keyId: string,
  isActive: boolean
): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${CUSTOMER_API_BASE_URL}/${customerId}/apiKeys/${keyId}`,
    {
      method: "PUT",
      headers,
      body: JSON.stringify({ isActive }),
    }
  );
  await handleResponse<{ success: boolean }>(response);
}

// ============================================
// 配信状況
// ============================================

/**
 * 配信レコード情報
 */
export interface DeliveryItem {
  id: string;
  filename: string;
  status: "pending" | "downloaded" | "missing" | "deleting" | "expired";
  createdAt: SerializedTimestamp;
  downloadedAt: SerializedTimestamp | null;
}

/**
 * 配信状況レスポンス
 */
export interface DeliveryStatus {
  pendingCount: number;
  deliveries: DeliveryItem[];
}

/**
 * 顧客の配信状況を取得
 */
export async function fetchDeliveryStatus(
  customerId: string
): Promise<DeliveryStatus> {
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${CUSTOMER_API_BASE_URL}/${customerId}/deliveries/status`,
    { headers }
  );
  return handleResponse<DeliveryStatus>(response);
}
